import { z } from "zod";

// API Key configuration schema
export const ApiKeyConfigSchema = z.object({
  name: z.string().trim().min(1, "API key name is required"),
  value: z.string().trim().min(1, "API key value is required"),
  location: z.enum(["header", "query"]).default("header"),
});

export type ApiKeyConfig = z.infer<typeof ApiKeyConfigSchema>;

// Basic Auth configuration schema
export const BasicAuthConfigSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

export type BasicAuthConfig = z.infer<typeof BasicAuthConfigSchema>;

// Bearer Token configuration schema
export const BearerTokenConfigSchema = z.object({
  token: z.string().min(1, "Token is required"),
  header_name: z.string().default("Authorization"),
});

export type BearerTokenConfig = z.infer<typeof BearerTokenConfigSchema>;

// OAuth2 configuration schema (authorization_code grant).
// client_id is OPTIONAL: DCR-only servers (e.g. Atlassian Rovo) ship no static
// Client ID — the executor registers one dynamically at connect time using the
// discovered registration_endpoint. When client_id is blank, registration_endpoint
// must be present for the connect to succeed.
// client_secret is OPTIONAL: public clients (PKCE) and the Claude-style
// "Add custom connector" flow may supply only a Client ID. Confidential
// clients still pass a secret; the executor treats an absent secret as a
// public client.
export const OAuth2ConfigSchema = z.object({
  client_id: z.string().optional(),
  client_secret: z.string().optional(),
  token_url: z.string().url("Invalid token URL"),
  authorization_url: z.union([z.string().url("Invalid authorization URL"), z.literal("")]).optional(),
  // RFC 7591 Dynamic Client Registration endpoint discovered from AS metadata.
  // Lets the executor register a client when no static client_id is supplied.
  registration_endpoint: z.union([z.string().url("Invalid registration URL"), z.literal("")]).optional(),
  scopes: z.array(z.string()).optional(),
  grant_type: z.string().optional(),
  // Zod v4: record(keySchema, valueSchema)
  auth_params: z.record(z.string(), z.string()).optional(),
});

export type OAuth2Config = z.infer<typeof OAuth2ConfigSchema>;

// OAuth2 Client-Initiated Multi-Device (CIMD) configuration schema.
// Public-client / no-secret flow where client_id is discovered via Dynamic
// Client Registration or pre-shared, and credentials are not stored here.
export const OAuth2CIMDConfigSchema = z.object({
  authorization_url: z.string().url("Invalid authorization URL"),
  token_url: z.string().url("Invalid token URL"),
  scopes: z.array(z.string()).optional(),
  prm_url: z.string().url().optional(),
});

export type OAuth2CIMDConfig = z.infer<typeof OAuth2CIMDConfigSchema>;

// OAuth2 Client Credentials grant configuration schema.
// Machine-to-machine flow with no user; tokens minted from client_id/secret.
export const OAuth2ClientCredentialsConfigSchema = z.object({
  client_id: z.string().min(1, "Client ID is required"),
  client_secret: z.string().min(1, "Client secret is required"),
  token_url: z.string().url("Invalid token URL"),
  audience: z.string().optional(),
  scopes: z.array(z.string()).optional(),
});

export type OAuth2ClientCredentialsConfig = z.infer<typeof OAuth2ClientCredentialsConfigSchema>;

// Scope at which credentials are stored / shared. Mirrors backend ScopeLevel.
export const ScopeLevelSchema = z.enum(["user", "workspace", "organization"]);
export type ScopeLevel = z.infer<typeof ScopeLevelSchema>;

// Persisted AuthConfig auth_type enum. Aligned with backend; intentionally
// excludes "not_configured" (a system-level virtual value handled in
// system-schemas.ts AuthTypeSchema, never stored on an AuthConfig row).
const AUTH_CONFIG_AUTH_TYPES = [
  "none",
  "api_key",
  "basic_auth",
  "bearer_token",
  "oauth2",
  "oauth2_cimd",
  "oauth2_client_credentials",
] as const;

type AuthConfigAuthType = (typeof AUTH_CONFIG_AUTH_TYPES)[number];

const AuthConfigObjectSchema = z.object({
  authconfig_id: z.string().optional(),
  system_id: z.string().min(1, "System ID is required"),
  auth_type: z.enum(AUTH_CONFIG_AUTH_TYPES),
  api_key: ApiKeyConfigSchema.optional(),
  basic_auth: BasicAuthConfigSchema.optional(),
  bearer_token: BearerTokenConfigSchema.optional(),
  oauth2: OAuth2ConfigSchema.optional(),
  oauth2_cimd: OAuth2CIMDConfigSchema.optional(),
  oauth2_client_credentials: OAuth2ClientCredentialsConfigSchema.optional(),
  scope_level: ScopeLevelSchema.optional(),
  workspace_id: z.string().optional(),
  organization_id: z.string().optional(),
  created_time: z.string().datetime().optional(),
  last_updated_time: z.string().datetime().optional(),
  is_encrypted: z.boolean().optional(),
});

const authTypeMatchesConfig = (
  data: {
    auth_type: AuthConfigAuthType;
    api_key?: unknown;
    basic_auth?: unknown;
    bearer_token?: unknown;
    oauth2?: unknown;
    oauth2_cimd?: unknown;
    oauth2_client_credentials?: unknown;
  },
) => {
  switch (data.auth_type) {
    case "api_key":
      return data.api_key !== undefined;
    case "basic_auth":
      return data.basic_auth !== undefined;
    case "bearer_token":
      return data.bearer_token !== undefined;
    case "oauth2":
      return data.oauth2 !== undefined;
    case "oauth2_cimd":
      return data.oauth2_cimd !== undefined;
    case "oauth2_client_credentials":
      return data.oauth2_client_credentials !== undefined;
    case "none":
      return true;
    default:
      return false;
  }
};

// Main AuthConfig schema (create/full payload)
export const AuthConfigSchema = AuthConfigObjectSchema.refine(
  (data) => authTypeMatchesConfig(data),
  {
    message: "Auth configuration must match the selected auth_type",
  },
);

// Update schema (partial payload) - only enforce auth_type/config match when auth_type is present
export const AuthConfigUpdateSchema = AuthConfigObjectSchema.partial().superRefine(
  (data, ctx) => {
    if (data.auth_type === undefined) {
      return;
    }
    if (
      !authTypeMatchesConfig({
        auth_type: data.auth_type,
        api_key: data.api_key,
        basic_auth: data.basic_auth,
        bearer_token: data.bearer_token,
        oauth2: data.oauth2,
        oauth2_cimd: data.oauth2_cimd,
        oauth2_client_credentials: data.oauth2_client_credentials,
      })
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Auth configuration must match the selected auth_type",
      });
    }
  },
);

export type AuthConfig = z.infer<typeof AuthConfigSchema>;

// Response schemas
export const AuthConfigResponseSchema = z.object({
  status: z.string(),
  message: z.string(),
  data: z.array(AuthConfigSchema),
  error_code: z.string().optional(),
});

export type AuthConfigResponse = z.infer<typeof AuthConfigResponseSchema>;

export const DeleteAuthConfigResponseSchema = z.object({
  status: z.string(),
  message: z.string(),
});

export type DeleteAuthConfigResponse = z.infer<
  typeof DeleteAuthConfigResponseSchema
>;

// API Response wrapper
export const ApiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    status: z.enum(["OK", "ERROR"]),
    data: z.array(dataSchema).optional(),
    message: z.string().optional(),
    total: z.number().int().min(0).optional(),
    page: z.number().int().min(1).optional(),
    pageSize: z.number().int().min(1).optional(),
  });

export type ApiResponse<T> = {
  status: "OK" | "ERROR";
  data?: T[];
  message?: string;
  total?: number;
  page?: number;
  pageSize?: number;
};
